import React, { useMemo } from 'react';

/** Two-letter badge: first char of first two words, or first two chars of single word */
export function assignmentCircleAbbrev(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  const t = parts[0] || label;
  return t.substring(0, 2).toUpperCase();
}

type Kind = 'position' | 'schedule';

interface AssignmentCircleLinkProps {
  kind: Kind;
  label: string;
  onClick: () => void;
  /** Default matches Task Admin table / modals */
  size?: 'sm' | 'md';
  title?: string;
}

/**
 * Circular badge (green = position, purple = schedule) like the original UI,
 * with underlined initials to show it opens the employee list.
 */
const AssignmentCircleLink: React.FC<AssignmentCircleLinkProps> = ({
  kind,
  label,
  onClick,
  size = 'md',
  title,
}) => {
  const abbrev = useMemo(() => assignmentCircleAbbrev(label), [label]);
  const isPosition = kind === 'position';
  const sizeCls =
    size === 'sm' ? 'w-6 h-6 min-w-[1.5rem] min-h-[1.5rem] text-[10px]' : 'w-8 h-8 min-w-[2rem] min-h-[2rem] text-xs';
  const colorCls = isPosition
    ? 'bg-green-500 hover:bg-green-600 focus-visible:ring-green-400'
    : 'bg-purple-500 hover:bg-purple-600 focus-visible:ring-purple-400';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${sizeCls} rounded-full text-white flex items-center justify-center font-semibold cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${colorCls} shrink-0 border-0 p-0 shadow-sm`}
      title={title ?? (isPosition ? `Position: ${label} — view employees` : `Schedule: ${label} — view employees`)}
    >
      <span className="underline decoration-white underline-offset-2 decoration-2">{abbrev}</span>
    </button>
  );
};

export default AssignmentCircleLink;
